import {
  MouseEvent,
  MouseEventHandler,
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { CardInfo } from '@zooplus/checkout-state';

import { CreditCardStatus } from '@types-internal';
import { CreditCard, CreditCardWithPaymentMethod } from '@types-internal/ModernizePaymentTypes';

import { OverlayIds, useOverlays } from './OverlaysProvider';
import { usePaymentContext } from './PaymentContextProvider';

type CreditCardContextData = {
  deleteCreditCard?: (creditCard?: CreditCardWithPaymentMethod) => MouseEventHandler<HTMLElement>;
  selectCreditCard?: (creditCard?: CreditCardWithPaymentMethod) => MouseEventHandler<HTMLElement>;
  deselectCreditCard?: () => void;
  deleteHandler?: () => void;
  status?: CreditCardStatus;
  cardToDelete?: CreditCard;
  creditCardsList?: CardInfo[];
  selectedCardIdentifier?: string;
  setCreditCardsList: (cardList: CardInfo[]) => void;
  getFlatCreditCardList: () => (CreditCard & { paymentMethod: string | null })[];
};

export type CreditCardContextProviderProps = {
  creditCardsList: CardInfo[];
  selectedCardIdentifier?: string;
};
export const CreditCardContext = createContext<CreditCardContextData>({
  setCreditCardsList: () => {},
  getFlatCreditCardList: () => [],
});
export const useCreditCardContext = () => useContext(CreditCardContext);

export const CreditCardContextProvider: React.FC<CreditCardContextProviderProps> = ({
  children,
  creditCardsList: initList,
  selectedCardIdentifier: initIdentifier,
}) => {
  const { showOverlay } = useOverlays();
  const { setSelectedPayment, deleteSavedCard, checkoutState } = usePaymentContext();
  const { paymentMethods, isPaymentCollapsed } = checkoutState || {};
  const CC = paymentMethods?.find((pm) => pm.paymentMethodShortName === 'K');

  const [creditCardsList, setCreditCardsList] = useState<CardInfo[]>(initList);
  const [selectedCardIdentifier, setSelectedCardIdentifier] = useState<string | undefined>();
  const [status, setStatus] = useState<CreditCardStatus>('idle');
  const [cardToDelete, setCardToDelete] = useState<CreditCardWithPaymentMethod>();

  useEffect(() => {
    setCreditCardsList(initList);
  }, [initList]);

  useEffect(() => {
    setSelectedCardIdentifier(initIdentifier);
  }, [initIdentifier, isPaymentCollapsed]);

  const deleteHandler = () => {
    if (cardToDelete) {
      deleteSavedCard(cardToDelete);
      if (cardToDelete.pspIdentifier === selectedCardIdentifier) {
        deselectCreditCard?.();
      }
    }
  };

  const deleteCreditCard =
    (creditCard?: CreditCardWithPaymentMethod) => (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (creditCard) {
        setCardToDelete(creditCard);
        showOverlay(OverlayIds.DELETE_CARD_NEW);
      }
    };
  const selectCreditCard = (creditCard?: CreditCardWithPaymentMethod) => () => {
    if (creditCard) {
      setStatus('loading');
      setSelectedPayment({
        paymentMethod: 'CREDIT_CARD',
        paymentMethodShortName: 'K',
        details: { ...(CC?.details ?? {}), cards: [creditCard] },
      });
      if (creditCard?.pspIdentifier) {
        setSelectedCardIdentifier(creditCard?.pspIdentifier);
      }
      setStatus('idle');
    }
  };

  const deselectCreditCard = () => {
    setSelectedCardIdentifier(undefined);
  };

  const getFlatCreditCardList = useCallback(() => {
    const flatCreditCardsList = creditCardsList
      ?.flatMap((cardInfo) =>
        (cardInfo.cards || []).map((card) => ({
          ...card,
          paymentMethod: cardInfo.paymentMethod,
        })),
      )
      .sort((a, b) => {
        if (a.pspIdentifier === '0') return -1;
        if (b.pspIdentifier === '0') return 1;
        return 0;
      });

    return flatCreditCardsList;
  }, [creditCardsList]);

  return (
    <CreditCardContext.Provider
      value={{
        deleteCreditCard,
        selectCreditCard,
        deselectCreditCard,
        deleteHandler,
        status,
        cardToDelete,
        creditCardsList,
        selectedCardIdentifier,
        setCreditCardsList,
        getFlatCreditCardList,
      }}>
      {children}
    </CreditCardContext.Provider>
  );
};

export default CreditCardContextProvider;
